import { useCallback, useMemo, useState } from 'react'

import { Skeleton } from 'antd'
import { formatDistanceToNow } from 'date-fns'
import { Icon } from 'govwell-ui'

import { navigate, routes, useParams } from '@redwoodjs/router'

import { EllipsisButton } from 'src/components/EllipsisButton'
import Page from 'src/components/shared/Page/Page'
import {
  useDeleteLetterheadMutation,
  useGetLetterheadByUuidQuery,
  useUpdateLetterheadMutation,
} from 'src/fetch/letterheads'
import { useSetRecordSettingsMutation } from 'src/fetch/recordSettings'
import { T } from 'src/govwell-ui'
import { ActionItem } from 'src/govwell-ui/types'
import useDisclosure from 'src/hooks/use-disclosure'

import DeleteLetterheadModal from './DeleteLetterheadModal'
import LetterheadForm from './LetterheadForm'
import LetterheadPreview from './LetterheadPreview'
import { getInitialFormValues, LetterheadFormValues } from './types'

const LetterheadPage = () => {
  const { uuid } = useParams()
  const { data, isLoading, refetch } = useGetLetterheadByUuidQuery(uuid)
  const letterhead = useMemo(() => data?.getLetterheadByUuid, [data])

  const { mutateAsync: updateLetterhead, isPending } = useUpdateLetterheadMutation()
  const { mutateAsync: deleteLetterhead } = useDeleteLetterheadMutation()
  const { mutateAsync: updateRecordSettings } = useSetRecordSettingsMutation()

  const deleteModalState = useDisclosure()

  const initialValues = useMemo(() => getInitialFormValues(letterhead), [letterhead])

  // Track form values for live preview
  const [formValues, setFormValues] = useState<LetterheadFormValues>(initialValues)

  const handleFormValuesChange = useCallback((values: LetterheadFormValues) => {
    setFormValues(values)
  }, [])

  const handleSubmit = useCallback(
    async (values: LetterheadFormValues) => {
      if (!letterhead?.id) {
        return
      }

      await updateLetterhead({
        id: letterhead.id,
        input: {
          backgroundColor: values.backgroundColor,
          center:
            values.centerType === 'File'
              ? { fileId: values.centerFile?.id ?? 0 }
              : { text: values.centerText },
          centerFileWidthPx: values.centerFileWidthPx,
          leftText: values.leftText,
          name: values.name,
          rightText: values.rightText,
        },
      })
      await refetch()
    },
    [letterhead?.id, updateLetterhead, refetch]
  )

  const handleSetAsOrgDefault = useCallback(async () => {
    if (!letterhead?.organization?.id || !letterhead?.id) {
      return
    }
    await updateRecordSettings({
      connection: { organizationId: letterhead.organization.id },
      settings: { defaultLetterheadId: letterhead.id },
    })
    await refetch()
  }, [letterhead?.id, letterhead?.organization?.id, refetch, updateRecordSettings])

  const handleDeleteConfirm = useCallback(async () => {
    if (!letterhead?.id) {
      return
    }
    await deleteLetterhead({ id: letterhead.id })
    navigate(routes.letterheads())
  }, [deleteLetterhead, letterhead?.id])

  const menuItems = useMemo((): ActionItem[] => {
    const items: ActionItem[] = []

    if (!letterhead?.isDefaultForOrganization) {
      items.push({
        label: 'Make default for organization',
        onClick: handleSetAsOrgDefault,
        icon: 'FaCheck',
        key: 'set-default',
      })
    }

    items.push({
      label: 'Delete',
      onClick: deleteModalState.open,
      icon: 'FaTrash',
      key: 'delete',
    })

    return items
  }, [deleteModalState.open, handleSetAsOrgDefault, letterhead?.isDefaultForOrganization])

  if (isLoading) {
    return (
      <Page>
        <Page.Header title="Letterhead" backButtonHref={routes.letterheads()} />
        <Page.Content>
          <Skeleton active />
        </Page.Content>
      </Page>
    )
  }

  if (!letterhead) {
    return (
      <Page>
        <Page.Header title="Letterhead Not Found" backButtonHref={routes.letterheads()} />
        <Page.Content>
          <p>The requested letterhead could not be found.</p>
        </Page.Content>
      </Page>
    )
  }

  const lastSavedSubtitle = letterhead.updatedAt ? (
    <T.BodySmall className="text-neutral-500">
      <Icon.FaCloudCheck className="mr-1 inline" />
      Last saved {formatDistanceToNow(new Date(letterhead.updatedAt), { addSuffix: true })}
    </T.BodySmall>
  ) : undefined

  const rightSidebar = (
    <div className="h-full overflow-y-auto border-l border-neutral-200 p-4">
      <LetterheadForm
        initialValues={initialValues}
        isSubmitting={isPending}
        onSubmit={handleSubmit}
        onFormValuesChange={handleFormValuesChange}
      />
    </div>
  )

  return (
    <Page pageOverflowType="PageContentOnly" rightSidebar={rightSidebar}>
      <Page.Header
        title={letterhead.name || 'Untitled Letterhead'}
        subtitle={lastSavedSubtitle}
        backButtonHref={routes.letterheads()}
        actions={<EllipsisButton items={menuItems} />}
      />
      <Page.Content className="flex items-center justify-center bg-neutral-50">
        <LetterheadPreview formValues={formValues} />
      </Page.Content>
      {deleteModalState.isOpen && (
        <DeleteLetterheadModal onDelete={handleDeleteConfirm} onClose={deleteModalState.close} />
      )}
    </Page>
  )
}

export default LetterheadPage
